import React, { useState } from 'react';
import { useQuery } from 'react-query';
import {
  ClipboardDocumentListIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  EyeIcon,
  XMarkIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import { requestAPI, formatDateTime } from '../services/api';
import LoadingSpinner from '../components/UI/LoadingSpinner';

const MyRequests = () => {
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Récupérer mes demandes
  const { data: requestsData, isLoading } = useQuery(
    'my-requests',
    () => requestAPI.getUserRequests(),
    {
      refetchInterval: 30000, // Rafraîchir toutes les 30 secondes
    }
  );

  const requests = requestsData?.data?.requests || [];

  const getStatusIcon = (status) => {
    switch (status) {
      case 'APPROUVEE':
        return <CheckCircleIcon className="h-5 w-5 text-green-500" />;
      case 'REJETEE':
        return <XCircleIcon className="h-5 w-5 text-red-500" />;
      case 'EN_ATTENTE':
        return <ClockIcon className="h-5 w-5 text-yellow-500" />;
      default:
        return <ClipboardDocumentListIcon className="h-5 w-5 text-gray-500" />;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'APPROUVEE': return 'success';
      case 'REJETEE': return 'danger';
      case 'EN_ATTENTE': return 'warning';
      default: return 'gray';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'APPROUVEE': return 'Approuvée';
      case 'REJETEE': return 'Rejetée';
      case 'EN_ATTENTE': return 'En attente';
      default: return status;
    }
  };

  const handleViewDetails = (request) => {
    setSelectedRequest(request);
    setShowDetailsModal(true);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* En-tête */}
      <div className="bg-white rounded-xl shadow-card p-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold text-primary-900 mb-2">
              Mes demandes
            </h1>
            <p className="text-gray-600">
              Consultez le statut de vos demandes de résiliation, suspension ou modification
            </p>
          </div>
          <button
            onClick={() => window.location.href = '/policies'}
            className="btn-primary flex items-center whitespace-nowrap"
          >
            <PlusIcon className="h-5 w-5 mr-2" />
            Faire une demande
          </button>
        </div>
      </div>

      {/* Résumé */}
      {requests.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="bg-white rounded-xl shadow-card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClipboardDocumentListIcon className="h-8 w-8 text-blue-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Total demandes</p>
                <p className="text-2xl font-semibold text-gray-900">{requests.length}</p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ClockIcon className="h-8 w-8 text-yellow-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">En attente</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {requests.filter(r => r.status === 'EN_ATTENTE').length}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow-card p-6">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-8 w-8 text-green-600" />
              </div>
              <div className="ml-4">
                <p className="text-sm font-medium text-gray-500">Approuvées</p>
                <p className="text-2xl font-semibold text-gray-900">
                  {requests.filter(r => r.status === 'APPROUVEE').length}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Liste des demandes */}
      <div className="bg-white rounded-xl shadow-card overflow-hidden">
        {requests.length === 0 ? (
          <div className="text-center py-12">
            <ClipboardDocumentListIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune demande</h3>
            <p className="mt-1 text-sm text-gray-500">
              Vous n'avez pas encore effectué de demande.
            </p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Police
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date de demande
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Statut
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {requests.map((request) => (
                  <tr key={request.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {getStatusIcon(request.status)}
                        <div className="ml-3">
                          <p className="text-sm font-medium text-gray-900">
                            {request.type?.libelle || 'N/A'}
                          </p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm text-gray-900">
                        {request.policy?.policyNumber || 'N/A'}
                      </div>
                      <div className="text-sm text-gray-500">
                        {request.policy?.subscription?.offer?.name || ''}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDateTime(request.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`badge badge-${getStatusColor(request.status)}`}>
                        {getStatusLabel(request.status)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <button
                        onClick={() => handleViewDetails(request)}
                        className="text-primary-600 hover:text-primary-900 flex items-center"
                      >
                        <EyeIcon className="h-4 w-4 mr-1" />
                        Voir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal de détails */}
      {showDetailsModal && selectedRequest && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={() => setShowDetailsModal(false)} />

            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-start justify-between mb-4">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Détails de la demande
                  </h3>
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  {/* Informations générales */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-3">Informations générales</h4>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs text-gray-500">Type de demande</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedRequest.type?.libelle || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Statut</p>
                        <span className={`badge badge-${getStatusColor(selectedRequest.status)}`}>
                          {getStatusLabel(selectedRequest.status)}
                        </span>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Date de demande</p>
                        <p className="text-sm font-medium text-gray-900">
                          {formatDateTime(selectedRequest.createdAt)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs text-gray-500">Police concernée</p>
                        <p className="text-sm font-medium text-gray-900">
                          {selectedRequest.policy?.policyNumber || 'N/A'}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Raison de la demande */}
                  <div className="bg-gray-50 rounded-lg p-4">
                    <h4 className="text-sm font-medium text-gray-900 mb-2">Raison de la demande</h4>
                    <p className="text-sm text-gray-600">
                      {selectedRequest.reasonText || 'Aucune raison fournie'}
                    </p>
                  </div>

                  {/* Notes additionnelles */}
                  {selectedRequest.notes && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-2">Notes additionnelles</h4>
                      <p className="text-sm text-gray-600">
                        {selectedRequest.notes}
                      </p>
                    </div>
                  )}

                  {/* Informations de traitement */}
                  {(selectedRequest.status === 'APPROUVEE' || selectedRequest.status === 'REJETEE') && (
                    <div className="bg-gray-50 rounded-lg p-4">
                      <h4 className="text-sm font-medium text-gray-900 mb-3">Traitement de la demande</h4>
                      <div className="space-y-3">
                        {selectedRequest.approvedBy && (
                          <div>
                            <p className="text-xs text-gray-500">Approuvée par</p>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedRequest.approvedBy?.email || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Le {formatDateTime(selectedRequest.approvedAt)}
                            </p>
                          </div>
                        )}
                        {selectedRequest.rejectedBy && (
                          <div>
                            <p className="text-xs text-gray-500">Rejetée par</p>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedRequest.rejectedBy?.email || 'N/A'}
                            </p>
                            <p className="text-xs text-gray-500">
                              Le {formatDateTime(selectedRequest.rejectedAt)}
                            </p>
                          </div>
                        )}
                        {selectedRequest.reason && (
                          <div>
                            <p className="text-xs text-gray-500">Motif de {selectedRequest.status === 'APPROUVEE' ? 'l\'approbation' : 'rejet'}</p>
                            <p className="text-sm font-medium text-gray-900">
                              {selectedRequest.reason?.libelle || 'N/A'}
                            </p>
                          </div>
                        )}
                        {selectedRequest.adminNotes && (
                          <div>
                            <p className="text-xs text-gray-500">Commentaire administrateur</p>
                            <p className="text-sm text-gray-600">
                              {selectedRequest.adminNotes}
                            </p>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-6">
                  <button
                    onClick={() => setShowDetailsModal(false)}
                    className="w-full btn-secondary"
                  >
                    Fermer
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default MyRequests;
